import { useState, useMemo } from "react";
import { X, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	ValidationWarnings,
	detectCircularDependency,
} from "./ValidationWarnings";
import type { Feature, FeatureDependency, PermissionAction } from "@/types";

const actionColors: Record<string, string> = {
	view: "bg-blue-100 text-blue-800 hover:bg-blue-200",
	create: "bg-green-100 text-green-800 hover:bg-green-200",
	update: "bg-yellow-100 text-yellow-800 hover:bg-yellow-200",
	delete: "bg-red-100 text-red-800 hover:bg-red-200",
	approve: "bg-purple-100 text-purple-800 hover:bg-purple-200",
	export: "bg-gray-100 text-gray-800 hover:bg-gray-200",
	manage: "bg-indigo-100 text-indigo-800 hover:bg-indigo-200",
};

interface DependencyEditorProps {
	feature: Feature;
	allFeatures: Feature[];
	dependencyMap: Record<string, FeatureDependency[]>;
	initialDependencies: FeatureDependency[];
	onSave: (dependencies: FeatureDependency[]) => Promise<void>;
	onCancel: () => void;
	isSaving?: boolean;
}

export function DependencyEditor({
	feature,
	allFeatures,
	dependencyMap,
	initialDependencies,
	onSave,
	onCancel,
	isSaving = false,
}: DependencyEditorProps) {
	const [dependencies, setDependencies] =
		useState<FeatureDependency[]>(initialDependencies);
	const [selectedFeature, setSelectedFeature] = useState<string>("");
	const [selectedAction, setSelectedAction] = useState<PermissionAction | "">(
		"",
	);
	const [reason, setReason] = useState("");
	const [moduleFilter, setModuleFilter] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState("");

	// Get unique modules
	const modules = useMemo(() => {
		const moduleSet = new Set(allFeatures.map((f) => f.module));
		return Array.from(moduleSet).sort();
	}, [allFeatures]);

	// Get available features (exclude self and already added)
	const availableFeatures = useMemo(() => {
		return allFeatures.filter((f) => {
			// Exclude self
			if (f.slug === feature.slug) return false;

			// Filter by module
			if (moduleFilter && f.module !== moduleFilter) return false;

			// Filter by search
			if (searchTerm) {
				const search = searchTerm.toLowerCase();
				if (
					!f.slug.toLowerCase().includes(search) &&
					!f.ui?.label?.toLowerCase().includes(search) &&
					!f.name?.toLowerCase().includes(search)
				) {
					return false;
				}
			}

			return true;
		});
	}, [allFeatures, feature.slug, moduleFilter, searchTerm, dependencies]);

	// Get available actions for selected feature
	const availableActions = useMemo(() => {
		if (!selectedFeature) return [];

		const feat = allFeatures.find((f) => f.slug === selectedFeature);
		if (!feat) return [];

		const addedActions = dependencies
			.filter((d) => d.feature === selectedFeature)
			.map((d) => d.action);

		return feat.actions.filter((a) => !addedActions.includes(a));
	}, [selectedFeature, allFeatures, dependencies]);

	// Check for circular dependency when adding
	const circularWarning = useMemo(() => {
		if (!selectedFeature || !selectedAction) return null;

		// Build a temporary dependency map with the new dependency
		const tempDependencyMap: Record<string, FeatureDependency[]> = {
			...dependencyMap,
		};

		// Add current feature's dependencies
		const currentDeps = [...dependencies];
		if (!currentDeps.some((d) => d.feature === selectedFeature)) {
			currentDeps.push({
				feature: selectedFeature,
				action: selectedAction,
			});
		}
		tempDependencyMap[feature.slug] = currentDeps;

		const chain = detectCircularDependency({
			featureCode: feature.slug,
			targetFeature: selectedFeature,
			dependencyMap: tempDependencyMap,
		});

		if (chain) {
			return {
				type: "circular" as const,
				message: `Adding this dependency would create a circular reference`,
				chain,
			};
		}

		return null;
	}, [selectedFeature, selectedAction, feature.slug, dependencyMap, dependencies]);

	const handleAddDependency = () => {
		if (!selectedFeature || !selectedAction || circularWarning) return;

		const newDep: FeatureDependency = {
			feature: selectedFeature,
			action: selectedAction,
			reason: reason || undefined,
		};

		setDependencies([...dependencies, newDep]);
		setSelectedFeature("");
		setSelectedAction("");
		setReason("");
	};

	const handleRemoveDependency = (index: number) => {
		setDependencies(dependencies.filter((_, i) => i !== index));
	};

	const handleSave = async () => {
		await onSave(dependencies);
	};

	const hasChanges =
		JSON.stringify(dependencies) !== JSON.stringify(initialDependencies);

	return (
		<div className="space-y-4 p-4 bg-blue-50/50 border border-blue-200 rounded-lg">
			<div className="flex items-center justify-between">
				<h4 className="text-sm font-medium">Edit Dependencies</h4>
				<div className="flex gap-2">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={onCancel}
						disabled={isSaving}
					>
						Cancel
					</Button>
					<Button
						type="button"
						size="sm"
						onClick={handleSave}
						disabled={isSaving || !hasChanges}
					>
						{isSaving ? (
							<>
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
								Saving...
							</>
						) : (
							"Save Changes"
						)}
					</Button>
				</div>
			</div>

			{/* Current Dependencies */}
			<div>
				<label className="text-xs font-medium text-muted-foreground mb-2 block">
					Current Dependencies ({dependencies.length})
				</label>
				<div className="flex flex-wrap gap-2 min-h-[32px]">
					{dependencies.length === 0 ? (
						<span className="text-sm text-muted-foreground italic">
							No dependencies defined
						</span>
					) : (
						dependencies.map((dep, index) => (
							<Badge
								key={`${dep.feature}:${dep.action}`}
								variant="outline"
								className={`${actionColors[dep.action] || ""} cursor-pointer group`}
							>
								<span className="font-mono text-xs">{dep.feature}</span>
								<span className="mx-1">:</span>
								<span>{dep.action}</span>
								{dep.reason && (
									<span
										className="ml-1 text-muted-foreground"
										title={dep.reason}
									>
										(*)
									</span>
								)}
								<button
									type="button"
									onClick={() => handleRemoveDependency(index)}
									className="ml-1 opacity-60 hover:opacity-100"
									title="Remove dependency"
								>
									<X className="h-3 w-3" />
								</button>
							</Badge>
						))
					)}
				</div>
			</div>

			{/* Add Dependency Form */}
			<div className="space-y-3 pt-2 border-t">
				<label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
					<Plus className="h-3 w-3" />
					Add Dependency
				</label>

				{/* Module Filter */}
				<div className="flex gap-1 flex-wrap">
					<Button
						type="button"
						variant={moduleFilter === null ? "default" : "outline"}
						size="sm"
						className="h-6 text-xs"
						onClick={() => setModuleFilter(null)}
					>
						All Modules
					</Button>
					{modules.map((module) => (
						<Button
							key={module}
							type="button"
							variant={moduleFilter === module ? "default" : "outline"}
							size="sm"
							className="h-6 text-xs"
							onClick={() => setModuleFilter(module)}
						>
							{module}
						</Button>
					))}
				</div>

				{/* Search and Selects */}
				<div className="flex gap-2 items-start">
					<div className="flex-1">
						<Input
							placeholder="Search features..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="h-9 mb-2"
						/>
						<Select
							value={selectedFeature}
							onValueChange={setSelectedFeature}
						>
							<SelectTrigger className="h-9">
								<SelectValue placeholder="Select feature" />
							</SelectTrigger>
							<SelectContent>
								{availableFeatures.map((f) => (
									<SelectItem key={f.slug} value={f.slug}>
										<span className="font-medium">
											{f.ui?.label || f.slug}
										</span>
										<span className="text-muted-foreground ml-2 text-xs font-mono">
											{f.slug}
										</span>
									</SelectItem>
								))}
								{availableFeatures.length === 0 && (
									<SelectItem value="" disabled>
										No features available
									</SelectItem>
								)}
							</SelectContent>
						</Select>
					</div>

					<div className="w-32">
						<div className="h-9 mb-2" /> {/* Spacer to align with search */}
						<Select
							value={selectedAction}
							onValueChange={(v) => setSelectedAction(v as PermissionAction)}
							disabled={!selectedFeature}
						>
							<SelectTrigger className="h-9">
								<SelectValue placeholder="Action" />
							</SelectTrigger>
							<SelectContent>
								{availableActions.map((action) => (
									<SelectItem key={action} value={action}>
										{action}
									</SelectItem>
								))}
								{availableActions.length === 0 && selectedFeature && (
									<SelectItem value="" disabled>
										All actions added
									</SelectItem>
								)}
							</SelectContent>
						</Select>
					</div>

					<div className="w-48">
						<div className="h-9 mb-2" /> {/* Spacer to align with search */}
						<Input
							placeholder="Reason (optional)"
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							className="h-9"
							disabled={!selectedFeature || !selectedAction}
						/>
					</div>

					<div>
						<div className="h-9 mb-2" /> {/* Spacer to align with search */}
						<Button
							type="button"
							size="sm"
							className="h-9"
							onClick={handleAddDependency}
							disabled={
								!selectedFeature || !selectedAction || !!circularWarning
							}
						>
							<Plus className="h-4 w-4" />
						</Button>
					</div>
				</div>

				{/* Circular Dependency Warning */}
				{circularWarning && (
					<ValidationWarnings warnings={[circularWarning]} />
				)}
			</div>

			{/* Unsaved Changes Indicator */}
			{hasChanges && (
				<div className="text-xs text-amber-600 flex items-center gap-1">
					<span className="w-2 h-2 rounded-full bg-amber-500" />
					You have unsaved changes
				</div>
			)}
		</div>
	);
}
